/**
 * Invitaciones y badge Pionero — Era Pionera.
 *
 * Autoridad: docs/adr/007-era-pionera.md
 *            docs/16-INVITATION-AND-PIONEERS.md (especificación completa)
 *
 * PRINCIPIO: las invitaciones se GANAN publicando, no se regalan al
 * registrarse. Quien no ha publicado nada no tiene qué mostrar y quema su
 * capital social invitando a un mapa vacío.
 *
 * ANTI-PIRÁMIDE: la única moneda por invitar es más capacidad de invitar.
 * Nunca XP, nunca reputación, nunca dinero. Profundidad 1.
 */

import { z } from 'zod';

// ─────────────────────────── Cupos ───────────────────────────

/** Los 25 primeros reciben invitaciones al registrarse. Alguien tiene que empezar. */
export const SEMILLA_CERO_MAX_USERS = 25;
export const SEMILLA_CERO_INVITES = 3;

/** Techo de invitaciones vivas simultáneas. Acumular no es estrategia. */
export const INVITE_CAP = 5;
export const INVITE_CAP_PIONERO = 7;

/** Vigencia de una invitación enviada. */
export const INVITE_VALIDITY_DAYS = 14;

/**
 * Cuánto dura una reserva durante el signup.
 *
 * 90 segundos, no 5 minutos: el signup termina en segundos, y una reserva
 * larga bloquea a quien cancela el flujo de OAuth y reintenta enseguida —
 * que es el caso más común que va a ocurrir.
 *
 * Es el número que convierte "invitación quemada para siempre" en
 * "invitación de vuelta en el pool en 90 segundos" cuando el alta falla a
 * medias. Ver docs/superpowers/spikes/2026-08-12-transaccionalidad-hook.md
 */
export const INVITE_RESERVATION_SECONDS = 90;

/** Máximo de invitaciones enviadas por ventana. Anti-spam (doc 09 §5). */
export const INVITE_MAX_SENT_PER_WEEK = 6;

/**
 * Invitaciones ganadas por hito de lugares publicados.
 * El hito de 3 es el más importante del sistema: el premio por aportar es
 * poder traer a alguien.
 */
export function invitesEarnedAtPlaceCount(placeCount: number): number {
  if (placeCount < 3) return 0;
  if (placeCount === 3) return 2;
  if (placeCount === 6) return 1;
  // Cada 3 lugares adicionales a partir del 9.
  if (placeCount >= 9 && (placeCount - 9) % 3 === 0) return 1;
  return 0;
}

/** Cupo total acumulado por publicar N lugares. */
export function totalInvitesEarned(placeCount: number): number {
  let total = 0;
  for (let n = 1; n <= placeCount; n++) total += invitesEarnedAtPlaceCount(n);
  return total;
}

// ─────────────────────── Código de invitación ───────────────────────

/**
 * Alfabeto Crockford Base32 sin caracteres ambiguos.
 * Sin 0/O, 1/I/L, U — el código tiene que poder dictarse por teléfono y
 * sobrevivir a que alguien lo escriba a mano.
 */
export const INVITE_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const INVITE_CODE_LENGTH = 6;

export const InviteCode = z
  .string()
  .length(INVITE_CODE_LENGTH)
  .regex(new RegExp(`^[${INVITE_CODE_ALPHABET}]+$`));

/** Normaliza lo que el usuario teclea: mayúsculas y ambigüedades corregidas. */
export function normalizeInviteCode(raw: string): string {
  return raw
    .trim()
    .toUpperCase()
    .replace(/[OÓ]/g, '0')
    .replace(/[IL]/g, '1')
    .replace(/U/g, 'V')
    .replace(/[^0-9A-Z]/g, '');
}

export const InviteState = z.enum(['PENDING', 'REDEEMED', 'EXPIRED', 'REVOKED']);
export type InviteState = z.infer<typeof InviteState>;

export const InviteError = z.enum([
  'invite_locked',
  'invite_expired',
  'invite_already_redeemed',
  'invite_revoked',
  'invite_not_found',
  'invite_same_device',
  'invite_cap_reached',
  'invite_rate_limited',
]);
export type InviteError = z.infer<typeof InviteError>;

// ─────────────────────────── Pionero ───────────────────────────

/** Lugares necesarios para el badge. */
export const PIONERO_PLACES_REQUIRED = 10;

/**
 * De esos 10, cuántos deben ser la PRIMERA recomendación del lugar.
 *
 * Sin esta condición, los últimos Pioneros solo recomendarían lo que otros
 * ya abrieron y "pionero" dejaría de describir nada. Con ella, Pionero es
 * por definición quien abrió territorio.
 */
export const PIONERO_FIRST_REQUIRED = 6;

/** Cupo nacional. Un "Pionero" del año 3 no es pionero. */
export const PIONERO_CUPO = 500;

/**
 * Piso temporal entre el primer y el décimo lugar (Q8).
 *
 * El filtro más potente y el más barato: convierte el badge de "sprint de
 * fin de semana" a hábito. Diego M. lo identificó como la respuesta
 * correcta a que farmear 10 tokens cuesta solo ~2 h de caminata.
 */
export const PIONERO_MIN_DAYS = 14;

/** Cuarentena tras el décimo lugar antes de otorgar (Q7). */
export const PIONERO_QUARANTINE_DAYS = 7;

/** Criterios de calidad por lugar (Q1–Q6). */
export const PIONERO_MIN_TEXT_LENGTH = 140;
export const PIONERO_MAX_SIMILARITY = 0.6;
export const PIONERO_MIN_GEO_CELLS = 4;
export const PIONERO_MIN_TAGS = 3;
export const PIONERO_MAX_CHAINS = 2;

/** Contribución de un lugar al progreso de Pionero. */
export const PioneroContribution = z.object({
  placeId: z.string().uuid(),
  publishedAt: z.string().datetime(),
  /** ¿Fue la primera recomendación que recibió este lugar? Inmutable. */
  wasFirstForPlace: z.boolean(),
  textLength: z.number().int().nonnegative(),
  hasPhoto: z.boolean(),
  /** Celda geográfica ~1 km, para medir dispersión. */
  geoCell: z.string(),
  tags: z.array(z.string()),
  isChain: z.boolean(),
  /** El token sigue vigente (no revocado). */
  tokenValid: z.boolean(),
});
export type PioneroContribution = z.infer<typeof PioneroContribution>;

export interface PioneroEvaluation {
  eligible: boolean;
  /** Lugares que cuentan (cumplen Q1–Q3 y token válido). */
  qualifying: number;
  /** De los que cuentan, cuántos fueron primera recomendación. */
  firsts: number;
  /** Razones por las que NO es elegible todavía. Vacío si lo es. */
  blockers: string[];
}

/**
 * Evalúa si un usuario merece el badge Pionero.
 *
 * Server-side y determinista: la misma entrada da el mismo veredicto.
 * `now` se recibe como parámetro para poder testear sin depender del reloj.
 */
export function evaluatePionero(
  contributions: readonly PioneroContribution[],
  now: Date,
): PioneroEvaluation {
  const blockers: string[] = [];

  // Q1–Q3 + token vigente: qué lugares cuentan siquiera.
  const qualifying = contributions.filter(
    (c) =>
      c.tokenValid &&
      c.hasPhoto &&
      c.textLength >= PIONERO_MIN_TEXT_LENGTH,
  );

  // Un mismo lugar no cuenta dos veces.
  const uniquePlaces = new Map(qualifying.map((c) => [c.placeId, c]));
  const unique = [...uniquePlaces.values()];

  if (unique.length < PIONERO_PLACES_REQUIRED) {
    blockers.push(
      `faltan ${PIONERO_PLACES_REQUIRED - unique.length} lugares que cumplan calidad`,
    );
  }

  const firsts = unique.filter((c) => c.wasFirstForPlace).length;
  if (firsts < PIONERO_FIRST_REQUIRED) {
    blockers.push(
      `faltan ${PIONERO_FIRST_REQUIRED - firsts} lugares donde seas el primero`,
    );
  }

  // Q4 — dispersión geográfica: mata "los 10 de mi cuadra".
  const cells = new Set(unique.map((c) => c.geoCell));
  if (cells.size < PIONERO_MIN_GEO_CELLS) {
    blockers.push('tus lugares están demasiado juntos');
  }

  // Q5 — dispersión temática.
  const tags = new Set(unique.flatMap((c) => c.tags));
  if (tags.size < PIONERO_MIN_TAGS) {
    blockers.push('falta variedad de tipos de comida');
  }

  // Q6 — anti-cadena: el producto prioriza rincones sobre lo masificado.
  const chains = unique.filter((c) => c.isChain).length;
  if (chains > PIONERO_MAX_CHAINS) {
    blockers.push('demasiadas cadenas');
  }

  // Q8 — piso temporal: convierte el badge en hábito, no en sprint.
  if (unique.length >= PIONERO_PLACES_REQUIRED) {
    const fechas = unique
      .map((c) => new Date(c.publishedAt).getTime())
      .sort((a, b) => a - b);
    const primero = fechas[0]!;
    const decimo = fechas[PIONERO_PLACES_REQUIRED - 1]!;
    const dias = (decimo - primero) / 86_400_000;

    if (dias < PIONERO_MIN_DAYS) {
      blockers.push(
        `faltan ${Math.ceil(PIONERO_MIN_DAYS - dias)} días: esto se gana con hábito`,
      );
    }

    // Q7 — cuarentena tras el décimo.
    const cuarentena = (now.getTime() - decimo) / 86_400_000;
    if (cuarentena < PIONERO_QUARANTINE_DAYS) {
      blockers.push('en revisión');
    }
  }

  return {
    eligible: blockers.length === 0,
    qualifying: unique.length,
    firsts,
    blockers,
  };
}

/**
 * Similitud Jaccard sobre trigramas (Q2 — anti-plantilla).
 *
 * Detecta el copy-paste entre los textos del mismo autor sin recurrir a
 * nada semántico: es O(n²) sobre 10 textos, o sea 45 comparaciones.
 */
export function jaccardTrigrams(a: string, b: string): number {
  const tri = (s: string): Set<string> => {
    const norm = s.toLowerCase().replace(/\s+/g, ' ').trim();
    const out = new Set<string>();
    for (let i = 0; i + 3 <= norm.length; i++) out.add(norm.slice(i, i + 3));
    return out;
  };

  const A = tri(a);
  const B = tri(b);
  if (A.size === 0 && B.size === 0) return 1;

  let inter = 0;
  for (const t of A) if (B.has(t)) inter++;
  const union = A.size + B.size - inter;
  return union === 0 ? 0 : inter / union;
}

/** ¿Algún par de textos supera el umbral de similitud? */
export function hasTemplateText(texts: readonly string[]): boolean {
  for (let i = 0; i < texts.length; i++) {
    for (let j = i + 1; j < texts.length; j++) {
      if (jaccardTrigrams(texts[i]!, texts[j]!) >= PIONERO_MAX_SIMILARITY) {
        return true;
      }
    }
  }
  return false;
}
